import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuthenticatedImageSrc } from "../../../hooks/useAuthenticatedImageSrc";
import { getPlanStableId } from "../../../data/dashboard-projects-dummy";
import styles from "./ProjectPickerCarousel.module.css";

function PlanSlide({ plan }) {
  const { t } = useTranslation("dashboard");
  const [imageFailed, setImageFailed] = useState(false);
  const imageSrc = useAuthenticatedImageSrc(plan?.projectImage, imageFailed);

  const title =
    (plan?.product || "").trim() || t("projectPicker.untitledProject");
  const sub =
    (plan?.projectUrl || "").trim() ||
    [plan?.subscriptionPlan || plan?.projectTier, plan?.projectType]
      .filter(Boolean)
      .join(" · ") ||
    t("projectPicker.noSubtitle");

  return (
    <article className={styles.slideCard}>
      <div className={styles.imageWrap}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            className={styles.image}
            onError={() => setImageFailed(true)}
            loading="lazy"
            width={640}
            height={360}
          />
        ) : (
          <div className={styles.imageFallback} aria-hidden />
        )}
      </div>
      <div className={styles.cardBody}>
        <h3 className={styles.projectName}>{title}</h3>
        <p className={styles.projectSub}>{sub}</p>
        {plan?.projectId ? (
          <p className={styles.projectMeta}>
            <span className={styles.metaLabel}>
              {t("projectPicker.projectId")}
            </span>
            {String(plan.projectId)}
          </p>
        ) : null}
      </div>
    </article>
  );
}

export default function ProjectPickerCarousel({
  plans = [],
  selectedPlanId,
  onSelect,
  embedded = false,
  matchProductsHeight = false,
  fullWidthEmbeddedViewport = false,
}) {
  const { t } = useTranslation("dashboard");

  const ids = useMemo(
    () => plans.map((p, i) => getPlanStableId(p, i)),
    [plans]
  );
  const activeIndex = useMemo(() => {
    const idx = ids.findIndex((id) => id === selectedPlanId);
    return Math.max(0, idx);
  }, [ids, selectedPlanId]);

  const activePlan = plans[activeIndex] || null;

  const go = (delta) => {
    if (plans.length < 2) return;
    const n = plans.length;
    const next = (activeIndex + delta + n) % n;
    const id = getPlanStableId(plans[next], next);
    if (id) onSelect?.(id);
  };

  const wideEmbedded =
    embedded && fullWidthEmbeddedViewport ? styles.sectionEmbeddedWide : "";

  const showArrows = plans.length > 1;
  const counter = t("projectPicker.slideCounter", {
    current: activeIndex + 1,
    total: plans.length,
  });

  if (!plans.length) {
    return (
      <section
        className={`${styles.section} ${embedded ? styles.sectionEmbedded : ""} ${
          embedded && matchProductsHeight ? styles.sectionEmbeddedMatch : ""
        } ${wideEmbedded}`}
        aria-labelledby="project-picker-heading"
      >
        <h2 id="project-picker-heading" className={styles.heading}>
          {t("projectPicker.title")}
        </h2>
        <p className={styles.empty}>{t("projectPicker.noProjects")}</p>
      </section>
    );
  }

  return (
    <section
      className={`${styles.section} ${embedded ? styles.sectionEmbedded : ""} ${
        embedded && matchProductsHeight ? styles.sectionEmbeddedMatch : ""
      } ${wideEmbedded}`}
      aria-labelledby="project-picker-heading"
    >
      <div className={styles.sectionHead}>
        <div>
          <h2 id="project-picker-heading" className={styles.heading}>
            {t("projectPicker.title")}
          </h2>
          <p className={styles.hint}>{t("projectPicker.hint")}</p>
        </div>
      </div>

      <div
        className={`${styles.viewport} ${embedded ? styles.viewportEmbedded : ""}`}
      >
        {activePlan ? <PlanSlide plan={activePlan} /> : null}
        <div
          className={`${styles.carouselControls} ${embedded ? styles.carouselControlsEmbedded : ""} ${styles.carouselControlsInViewport}`}
        >
          <span className={styles.counter}>{counter}</span>
          {showArrows ? (
            <div className={styles.arrows}>
              <button
                type="button"
                className={styles.arrowBtn}
                aria-label={t("projectPicker.scrollPrev")}
                onClick={() => go(-1)}
              >
                <ChevronLeft size={22} strokeWidth={2} />
              </button>
              <button
                type="button"
                className={styles.arrowBtn}
                aria-label={t("projectPicker.scrollNext")}
                onClick={() => go(1)}
              >
                <ChevronRight size={22} strokeWidth={2} />
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
